"use client";

import * as React from "react";
import { Tagline } from "@/components/pro-blocks/landing-page/tagline";
import { LeftRight } from "@/components/pro-blocks/landing-page/feature-sections/left-right";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import { cn } from "@/lib/utils";
import type { News, Page } from "@/payload-types";
import type { DefaultTypedEditorState } from "@payloadcms/richtext-lexical";
import type { MediaObjectFit } from "@/utilities/mediaObjectFit";

export type GallerySection10Link = {
  type?: "custom" | "reference" | null;
  newTab?: boolean | null;
  reference?: {
    relationTo: "pages" | "news";
    value: Page | News | string | number;
  } | null;
  url?: string | null;
  label?: string | null;
  appearance?: "default" | "outline" | "underline" | null;
};

export type GallerySection10Slide = {
  title?: string;
  body?: string | DefaultTypedEditorState;
  bodyAlignment?: "top" | "middle" | "bottom";
  links?: GallerySection10Link[];
  imagePosition?: "left" | "right";
  imageAlt?: string;
  imageTitle?: string;
  imageSrc?: string;
  imageObjectFit?: MediaObjectFit;
  backgroundImageTitle?: string;
  backgroundImageSrc?: string;
  backgroundImageObjectFit?: MediaObjectFit;
};

interface GallerySection10Props {
  tagline?: string;
  title?: string;
  body?: string;
  bodyAlignment?: "top" | "middle" | "bottom";
  titleTag?: "h1" | "h2" | "h3";
  titleClassName?: string;
  slides?: GallerySection10Slide[];
}

export function GallerySection10({
  tagline,
  title,
  body,
  bodyAlignment = "top",
  titleTag = "h2",
  titleClassName = "heading-lg",
  slides = [],
}: GallerySection10Props) {
  const TitleTag = titleTag;
  const [api, setApi] = React.useState<CarouselApi>();
  const [current, setCurrent] = React.useState(0);
  const [count, setCount] = React.useState(0);

  React.useEffect(() => {
    if (!api) {
      return;
    }

    setCount(api.scrollSnapList().length);
    setCurrent(api.selectedScrollSnap() + 1);

    api.on("select", () => {
      setCurrent(api.selectedScrollSnap() + 1);
    });
  }, [api]);

  if (!slides.length) return null;

  return (
    <section className="bg-background section-padding-y">
      <div className="container-padding-x container mx-auto flex flex-col gap-10 md:gap-7 2xl:gap-6">
        <div className="section-title-gap-lg flex max-w-xl flex-col items-start text-left">
          {tagline ? <Tagline>{tagline}</Tagline> : null}
          <div className="flex flex-col gap-2 pb-6:last">
            {title ? (
              <TitleTag className={`${titleClassName} text-foreground`}>
                {title}
              </TitleTag>
            ) : null}
            {body ? (
              <p className={`${titleClassName} text-muted-foreground`}>
                {body}
              </p>
            ) : null}
          </div>
        </div>
        <Carousel
          setApi={setApi}
          opts={{
            align: "start",
            loop: true,
          }}
          className="mb-12 w-full"
        >
          <div className="mb-4 flex items-center gap-2 md:hidden">
            <CarouselPrevious className="static top-0 bottom-0 h-10 w-10 translate-y-0" />
            <div className="text-foreground flex min-w-14 items-center justify-center text-sm font-medium">
              {current} / {count}
            </div>
            <CarouselNext className="static top-0 bottom-0 h-10 w-10 translate-y-0" />
          </div>
          <CarouselContent className="items-stretch md:items-start">
            {slides.map((slide, index) => {
              const selectedSnapIndex = current - 1;
              const indexOfSlideToMakeDim =
                count > 0 ? (selectedSnapIndex + 1) % count : -1;

              return (
                <CarouselItem
                  key={index}
                  className={cn(
                    "flex pl-4 md:block",
                    "basis-19/20",
                    index === indexOfSlideToMakeDim && count > 1
                      ? "opacity-50"
                      : "",
                  )}
                >
                  <div className="w-full [&>section]:h-full [&>section>div]:h-full md:[&>section]:h-auto md:[&>section>div]:h-auto">
                    <LeftRight
                      noPadding={true}
                      title={slide.title}
                      body={slide.body}
                      bodyAlignment={slide.bodyAlignment ?? bodyAlignment}
                      links={slide.links}
                      imagePosition={slide.imagePosition ?? "left"}
                      imageAlt={slide.imageAlt}
                      imageTitle={slide.imageTitle}
                      imageSrc={slide.imageSrc}
                      imageObjectFit={slide.imageObjectFit}
                      backgroundImageTitle={slide.backgroundImageTitle}
                      backgroundImageSrc={slide.backgroundImageSrc || ""}
                      backgroundImageObjectFit={slide.backgroundImageObjectFit}
                    />
                  </div>
                </CarouselItem>
              );
            })}
          </CarouselContent>
          <div className="absolute right-0 -top-16 hidden gap-2 md:flex">
            <CarouselPrevious className="static top-0 bottom-0 h-10 w-10 translate-y-0" />
            <div className="text-foreground flex min-w-14 items-center justify-center text-sm font-medium">
              {current} / {count}
            </div>
            <CarouselNext className="static top-0 bottom-0 h-10 w-10 translate-y-0" />
          </div>
          <div className="absolute left-1/2 -bottom-12 -translate-x-1/2 py-4 md:-bottom-16">
            <div className="flex items-center justify-center gap-2">
              {Array.from({ length: count }).map((_, index) => (
                <button
                  key={index}
                  onClick={() => api?.scrollTo(index)}
                  className={cn(
                    "h-2 w-2 rounded-full",
                    current === index + 1 ? "bg-primary" : "bg-muted",
                  )}
                  aria-label={`Go to slide ${index + 1}`}
                />
              ))}
            </div>
          </div>
        </Carousel>
      </div>
    </section>
  );
}
